// Write hook for working papers.
//
// `useUpsertWorkingPaper` wraps the insert-or-update wrapper, taking the
// transaction `referenceNo` + the working-paper fields. On success it invalidates
// the working-papers list and the by-tx entry. Errors surface via a toast and
// remain on the hook (`error`/`isError`).
import { useMutation } from '@tanstack/react-query';
import { queryClient } from '../lib/queryClient';
import { queryKeys, invalidateRegister } from '../lib/queryKeys';
import { useToast } from '../components/ui/Toast';
import {
  upsertWorkingPaperForTx,
  syncWorkingPaperReviewByTx,
  type WorkingPaperInput,
} from '../lib/dbWrite';

interface UpsertWorkingPaperArgs {
  referenceNo: string;
  input: WorkingPaperInput;
}

interface SyncWorkingPaperReviewArgs {
  /** The transaction's UUID — the FK the working paper is keyed on. */
  transactionId: string;
  /** The transaction's referenceNo — for invalidating its by-tx WP cache entry. */
  referenceNo: string;
  reviewStatus: string;
  /** A set Compliance override to mirror onto the paper's level (29 Sep 2026); omitted
   *  leaves the paper's level alone. */
  complianceLevel?: string;
}

export function useUpsertWorkingPaper() {
  const { show } = useToast();
  return useMutation({
    mutationFn: ({ referenceNo, input }: UpsertWorkingPaperArgs) =>
      upsertWorkingPaperForTx(referenceNo, input),
    onSuccess: (_id, { referenceNo }) => {
      // A working-paper save dual-writes review fields onto the transaction, so
      // the whole register surface (incl. scoped reviewers) must refetch.
      invalidateRegister(queryClient);
      void queryClient.invalidateQueries({ queryKey: queryKeys.workingPaperByTx(referenceNo) });
    },
    onError: (error: unknown) => {
      show(error instanceof Error ? error.message : 'Failed to save the working paper.', 'error');
    },
  });
}

/**
 * Mirror a transaction's review status onto its working paper (by transaction id) —
 * used by the Transaction editor so an un-review made from the register also clears the
 * working paper's stale "Reviewed" status. A no-op when the transaction has no paper.
 */
export function useSyncWorkingPaperReview() {
  const { show } = useToast();
  return useMutation({
    mutationFn: ({ transactionId, reviewStatus, complianceLevel }: SyncWorkingPaperReviewArgs) =>
      syncWorkingPaperReviewByTx(transactionId, reviewStatus, complianceLevel),
    onSuccess: (_v, { referenceNo }) => {
      invalidateRegister(queryClient);
      void queryClient.invalidateQueries({ queryKey: queryKeys.workingPaperByTx(referenceNo) });
    },
    onError: (error: unknown) => {
      show(error instanceof Error ? error.message : 'Failed to sync the review status.', 'error');
    },
  });
}
